import {
	AppBar,
	CircularProgress,
	Grid,
	IconButton,
	makeStyles,
	Tab,
	Tabs,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
	withStyles,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CachedIcon from '@material-ui/icons/Cached'
import CloudDoneOutlinedIcon from '@material-ui/icons/CloudDoneOutlined'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useMatch, useNavigate } from 'react-router'
import GridNavbarActions from 'src/components/ui/Actions/NavbarActions/GridNavbarActions'
import GridNavbarMobileActions from 'src/components/ui/Actions/NavbarActions/GridNavbarMobileActions'
import { useFetchBasicCompanyQuoteTemplatesQuery } from 'src/redux/api/graphql/quoteApi'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import { logOutUser } from 'src/redux/slices/auth'
import {
	calculateMarginsAndTotals,
	clearQuote,
	updateQuote,
} from 'src/redux/slices/quotes'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'
import { getCurrencySymbol } from 'src/utils/miscellaneous'
import getQuoteTotals from 'src/utils/quoteGridUtils/getQuoteTotals'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
	},
	iconImage: {
		flexGrow: 1,
	},
	logoImage: {
		height: '2.6rem',
		[theme.breakpoints.down('xs')]: {
			height: '1rem',
		},
	},
	appbar: {
		boxShadow: 'none',
	},
	container: {
		display: 'flex',
		width: '100%',
		height: '100%',
	},
	tabContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'start',
		alignItems: 'flex-end',
	},
	rootTabs: {
		minHeight: 'unset',
		width: '100%',
	},
	tabs: {
		minHeight: 'unset',
		height: '1.75rem',
		borderRadius: '8px 8px 0px 0px',
		minWidth: 'fit-content',

		'&> div': {
			height: '100%',
			maxHeight: '100%',
			'&> div': {
				height: '100%',
				maxHeight: '100%',
			},
		},

		'& a': {
			minWidth: '60px',
		},
	},
	activeTab: {
		backgroundColor: '#F9FAFB',
		color: '#000',
		borderRadius: '8px 8px 0px 0px',
		minHeight: 'unset',
	},
	inactiveTab: {
		background: '#2E3853',
		color: '#fff',
		opacity: 1,
		minHeight: 'unset',
	},
	topContainer: {
		display: 'flex',
		alignItems: 'center',
	},
	headingContainer: {
		display: 'flex',
		alignItems: 'center',
	},
	heading: {
		fontSize: '1rem',
		fontWeight: 600,
		marginLeft: '0.25rem',
		minWidth: '2rem',
		maxWidth: '30rem',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	exitButton: {
		color: '#fff',
		boxShadow: 'none',
		marginLeft: '0.25rem',
		marginRight: '0.5rem',
		'&:hover': {
			background: '#494A63',
			opacity: 0.9,
		},
		'&:disabled': {
			color: '#94A3B8',
			background: '#5E6782',
		},
		textTransform: 'unset',
		margin: 'auto 0',
	},
	saveIcon: {
		display: 'flex',
		alignItems: 'center',
		margin: '0 1rem ',
		height: '1.75rem',
		'& svg': {
			marginRight: '0.5rem',
		},
	},
	menuIcons: {
		display: 'flex',
		marginLeft: 'auto',
		justifyContent: 'end',
	},
	totalContainer: {
		marginLeft: 'auto',
		height: '100%',
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'flex-end',
		flexWrap: 'wrap',
		flexBasis: 'fit-content',
		gap: '1rem',
	},
	totalChips: {
		fontSize: '0.75rem',
		maxWidth: '15rem',
	},
	// '@media (max-width: 1350px)': {
	// 	totalContainer: {
	// 		marginLeft: '1rem',
	// 		gap: '0.5rem',
	// 	},
	// 	totalChips: {
	// 		width: 'auto',
	// 	},
	// },
	'@media (max-width: 1115px)': {
		heading: {
			maxWidth: '60%',
			fontSize: '1rem',
		},
	},
	'@media (max-width: 793px)': {
		heading: {
			maxWidth: '40%',
			fontSize: '1rem',
		},
	},
	'@media (max-width: 660px)': {
		heading: {
			maxWidth: '40%',
			fontSize: '1rem',
		},
		tabs: {
			marginLeft: 'unset',
			maxHeight: '35px',
			minHeight: 'unset',
			'& a': {
				minHeight: 'unset',
				minWidth: 'unset',
			},
		},
	},
	'@media (max-width: 1023px)': {
		menuIcons: {
			marginLeft: 0,
			marginBottom: '0.5rem',
		},

		exitButton: {
			fontSize: '0.75rem',
		},
	},
	'@media (max-width: 349px)': {
		tabs: {
			marginLeft: '0',
		},
		heading: {
			maxWidth: '60%',
			fontSize: '0.75rem',
		},
		exitButton: {
			fontSize: '0.5rem',
		},
	},
}))
interface StyledTabsProps {
	value: number
	className?: string
	rootClassName?: string
	children?: React.ReactNode
}

const StyledTabs = withStyles({
	indicator: {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		height: '100%',
		'& > span': {
			maxWidth: 40,
			height: '100%',
			// width: '100%',
		},
	},
})((props: StyledTabsProps) => (
	<Tabs
		classes={{ root: props.rootClassName }}
		{...props}
		TabIndicatorProps={{ children: <span /> }}
	/>
))

interface LinkTabProps extends React.ComponentProps<typeof Tab> {
	href: string
}

const CustomTab = withStyles({
	root: {
		textTransform: 'none',
	},
})(Tab)

function LinkTab(props: any) {
	return <CustomTab component={Link} to={props.href} {...props} />
}

function GridNavbar() {
	const classes = useStyles()
	const dispatch = useAppDispatch()
	const dispatchThunk = useAppThunkDispatch()
	const navigate = useNavigate()
	// GridNavbar is rendered by the MainLayout layout route, so useParams()
	// can't see the :quoteId matched by the descendant quote routes — match
	// the URL directly instead.
	const quoteId = useMatch('/quote/v1/:quoteId/*')?.params.quoteId
	const isSmall = useMediaQuery('(min-width: 900px)')
	const isBig = useMediaQuery('(min-width: 1400px)')
	const isMedium = useMediaQuery('(min-width: 1200px)')
	const linkSmall = useMediaQuery('(min-width: 1054px)')
	const totalChipSmall = useMediaQuery('(min-width: 854px)')

	const currentQuote = useAppSelector(
		(state) => state.quotes.present.currentQuote
	)
	const quoteInfo = useAppSelector((state) => state.quotes.present.quoteInfo)
	const maximizeMinimize = useAppSelector(
		(state) => state.quotes.present.maximizeMinimize
	)
	const updateQuoteInfoDebounceApiStatus = useAppSelector(
		(state) => state.quotes.present.updateQuoteInfoDebounceApiStatus
	)
	const updateQuoteInfoApiCounter = useAppSelector(
		(state) => state.quotes.present.updateQuoteInfoApiCounter
	)
	const updateRecapQuoteInfoDebounceApiStatus = useAppSelector(
		(state) => state.quotes.present.updateRecapQuoteInfoDebounceApiStatus
	)
	const updateRecapQuoteInfoApiCounter = useAppSelector(
		(state) => state.quotes.present.updateRecapQuoteInfoApiCounter
	)
	const quoteTotals = getQuoteTotals(currentQuote, quoteInfo)
	const currencySymbol = getCurrencySymbol(currentQuote)
	const grandTotal = quoteTotals?.grandTotal?.toLocaleString(undefined, {
		maximumFractionDigits: 2,
	})
	const subTotal = quoteTotals?.subTotal?.toLocaleString(undefined, {
		maximumFractionDigits: 2,
	})
	const totalMarginAndOverhead =
		quoteTotals?.totalMarginAndOverhead?.toLocaleString(undefined, {
			maximumFractionDigits: 2,
		})
	const totalMarginAndOverheadPercentage =
		quoteTotals?.totalMarginAndOverheadPercentage?.toLocaleString(
			undefined,
			{
				maximumFractionDigits: 2,
			}
		)
	const totalFrance = quoteTotals?.totalFrance?.toLocaleString(undefined, {
		maximumFractionDigits: 2,
	})
	const totalInternational = quoteTotals?.totalInternational?.toLocaleString(
		undefined,
		{
			maximumFractionDigits: 2,
		}
	)
	const totalInternationalLocalCurrency =
		quoteTotals?.totalInternationalLocalCurrency?.toLocaleString(
			undefined,
			{
				maximumFractionDigits: 2,
			}
		)
	const exchangeRate = currentQuote?.exchangeRate?.toLocaleString(undefined, {
		maximumFractionDigits: 2,
	})
	const userInfoId = useAppSelector((state) => state.auth.userInfo?.id)
	const {
		data: userInfo,
		isSuccess,
		isFetching,
	} = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId!,
		},
		{
			skip: !userInfoId,
		}
	)
	const {
		data: companyQuoteTemplateData,
		error: companyQuoteTemplateError,
		isLoading: companyQuoteTemplateLoading,
	} = useFetchBasicCompanyQuoteTemplatesQuery(
		{
			tenantId: userInfo?.currentCompanyTenantId!,
			userInfo: userInfo!,
		},
		{
			skip: !userInfo?.currentCompanyTenantId,
		}
	)
	const isTemplateQuote =
		companyQuoteTemplateData?.companyQuoteTemplateData?.some(
			(template) => template.quoteId === currentQuote?.id
		) ?? false

	const location = useLocation()
	const { t } = useTranslation()
	const { t: tProjectChildren } = useTranslation('projectChildren')
	const { t: tGridNavbar } = useTranslation('GridNavbar')
	const [showSpinner, setShowSpinner] = useState(false)

	// Compute tab value directly from pathname instead of storing in state
	const value = location.pathname.endsWith('budget')
		? 0
		: location.pathname.endsWith('prep')
			? 1
			: location.pathname.endsWith('wrap')
				? 2
				: location.pathname.endsWith('pivot')
					? 3
					: location.pathname.endsWith('gantt')
						? 4
						: 0

	const handleLogout = () => {
		dispatchThunk(logOutUser())
		navigate('/', { replace: true })
		Sentry.setUser(null)
	}

	const handleExit = () => {
		if (
			updateQuoteInfoDebounceApiStatus === 'loading' ||
			updateQuoteInfoApiCounter > 0 ||
			updateRecapQuoteInfoDebounceApiStatus === 'loading' ||
			updateRecapQuoteInfoApiCounter > 0
		) {
			setShowSpinner(true)
			setTimeout(() => {
				setShowSpinner(false)
			}, 3000)
			return
		}
		setShowSpinner(false)
		if (!quoteId) return
		// We are setting the users maximize minimize when exiting quote so that it is saved when they open the quote back again
		dispatchThunk(
			updateQuote({
				quoteId,
				newQuote: { maximizeMinimize },
				modifier: 'dontSetCurrentQuote',
			})
		)
		dispatchThunk(calculateMarginsAndTotals({ quoteId }))
		dispatch(clearQuote())
		if (currentQuote?.parentProjectId && !isTemplateQuote) {
			navigate(`/projects/${currentQuote?.parentProjectId}`)
		} else if (isTemplateQuote) {
			navigate('/companyTemplate')
		} else {
			navigate('/')
		}
	}

	const getQuoteName = ({
		quoteName,
		quoteType,
	}: {
		quoteName: string
		quoteType: string
	}) => {
		if (quoteType === 'invoiceQuote') {
			const subStrName = quoteName.substring(13)
			return `${tProjectChildren('quote.invoiceQuote')}${subStrName}`
		}
		return quoteName
	}

	function getQuotePills() {
		if (!quoteTotals || !location.pathname.endsWith('budget')) return <></>
		if (isBig) {
			return (
				<div className={classes.totalContainer}>
					<Typography className={classes.totalChips}>
						{`Total (${currencySymbol}) : ${grandTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`Sub-Total (${currencySymbol}) : ${subTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`MG + OH (${currencySymbol}) : ${totalMarginAndOverhead} (${totalMarginAndOverheadPercentage}%)`}
					</Typography>
					{currentQuote?.allowInternational && (
						<>
							<Typography className={classes.totalChips}>
								{`Lieu 1 (${currencySymbol}) : ${totalFrance}`}
							</Typography>
							<Typography className={classes.totalChips}>
								{`Lieu 2 (${currencySymbol}) :  ${totalInternational}`}
							</Typography>
							<Typography className={classes.totalChips}>
								{`Lieu 2 (LC) : ${totalInternationalLocalCurrency}`}
							</Typography>
							<Typography className={classes.totalChips}>
								{`E/R : ${exchangeRate}`}
							</Typography>
						</>
					)}
				</div>
			)
		}
		if (isMedium) {
			return (
				<div className={classes.totalContainer}>
					<Typography className={classes.totalChips}>
						{`Total (${currencySymbol}) : ${grandTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`Sub-Total (${currencySymbol}) : ${subTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`MG + OH (${currencySymbol}) : ${totalMarginAndOverhead} (${totalMarginAndOverheadPercentage}%)`}
					</Typography>
				</div>
			)
		}
		if (totalChipSmall) {
			return (
				<div className={classes.totalContainer}>
					<Typography className={classes.totalChips}>
						{`Ttl (${currencySymbol}) : ${grandTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`SubT (${currencySymbol}) : ${subTotal}`}
					</Typography>
					<Typography className={classes.totalChips}>
						{`MG + OH (${currencySymbol}) : ${totalMarginAndOverhead} (${totalMarginAndOverheadPercentage}%)`}
					</Typography>
				</div>
			)
		}
		return <></>
	}

	return (
		<AppBar position="static" className={classes.appbar}>
			<Toolbar variant="dense">
				<Grid container className={classes.container}>
					{/* 2 layers  */}
					<Grid item xs={10} md={10} className={classes.topContainer}>
						<div className={classes.headingContainer}>
							<Tooltip title={tGridNavbar('exitQuote')}>
								<IconButton
									onClick={handleExit}
									size="small"
									className={classes.exitButton}
									data-testid="exitQuoteButton"
								>
									<ArrowBackIcon />
								</IconButton>
							</Tooltip>
							{showSpinner && (
								<CircularProgress
									style={{ marginLeft: 10 }}
									size={20}
								/>
							)}

							<Typography
								className={classes.heading}
								data-testid="quoteName"
							>
								{getQuoteName({
									quoteName: currentQuote?.name as string,
									quoteType: currentQuote?.type as string,
								})}
							</Typography>
						</div>
					</Grid>
					<Grid item xs={2} md={2}>
						{!isSmall ? (
							<GridNavbarMobileActions
								handleLogout={handleLogout}
								isSmall={isSmall}
							/>
						) : (
							<GridNavbarActions
								handleLogout={handleLogout}
								isSmall={isSmall}
							/>
						)}
					</Grid>

					<Grid item xs={12} className={classes.tabContainer}>
						<StyledTabs
							value={value}
							aria-label="nav tabs example"
							className={classes.tabs}
						>
							<LinkTab
								className={`${
									value === 0
										? classes.activeTab
										: classes.inactiveTab
								}`}
								label={
									<Tooltip
										title={tGridNavbar('budget')}
										arrow
									>
										{!linkSmall && value !== 0 ? (
											<span>
												{tGridNavbar('budgetShort')}
											</span>
										) : (
											<span>{tGridNavbar('budget')}</span>
										)}
									</Tooltip>
								}
								href={`/quote/v1/${quoteId}/budget`}
							/>
							<LinkTab
								className={`${
									value === 1
										? classes.activeTab
										: classes.inactiveTab
								}`}
								label={
									<Tooltip title={tGridNavbar('prep')} arrow>
										{!linkSmall && value !== 1 ? (
											<span>
												{tGridNavbar('prepShort')}
											</span>
										) : (
											<span>{tGridNavbar('prep')}</span>
										)}
									</Tooltip>
								}
								href={`/quote/v1/${quoteId}/prep`}
							/>
							<LinkTab
								className={`${
									value === 2
										? classes.activeTab
										: classes.inactiveTab
								}`}
								label={
									<Tooltip title={tGridNavbar('wrap')} arrow>
										{!linkSmall && value !== 2 ? (
											<span>
												{tGridNavbar('wrapShort')}
											</span>
										) : (
											<span>{tGridNavbar('wrap')}</span>
										)}
									</Tooltip>
								}
								href={`/quote/v1/${quoteId}/wrap`}
							/>

							<LinkTab
								className={`${
									value === 3
										? classes.activeTab
										: classes.inactiveTab
								}`}
								label={
									<Tooltip title={tGridNavbar('pivot')} arrow>
										{!linkSmall && value !== 3 ? (
											<span>
												{tGridNavbar('pivotShort')}
											</span>
										) : (
											<span>{tGridNavbar('pivot')}</span>
										)}
									</Tooltip>
								}
								href={`/quote/v1/${quoteId}/pivot`}
							/>
							<LinkTab
								className={`${
									value === 4
										? classes.activeTab
										: classes.inactiveTab
								}`}
								label={
									<Tooltip
										title={tGridNavbar('schedule')}
										arrow
									>
										{!linkSmall && value !== 4 ? (
											<span>
												{tGridNavbar('scheduleShort')}
											</span>
										) : (
											<span>
												{tGridNavbar('schedule')}
											</span>
										)}
									</Tooltip>
								}
								href={`/quote/v1/${quoteId}/gantt`}
							/>
						</StyledTabs>

						{location.pathname.endsWith('budget') && (
							<div className={classes.saveIcon}>
								{updateQuoteInfoDebounceApiStatus ===
									'loading' ||
								updateQuoteInfoApiCounter > 0 ? (
									<>
										<CachedIcon fontSize="small" />{' '}
										{linkSmall && 'Saving...'}
									</>
								) : (
									<>
										<CloudDoneOutlinedIcon fontSize="small" />{' '}
										{linkSmall && 'Saved'}
									</>
								)}
							</div>
						)}
						{(location.pathname.endsWith('prep') ||
							location.pathname.endsWith('wrap') ||
							location.pathname.endsWith('pivot')) && (
							<div className={classes.saveIcon}>
								{updateRecapQuoteInfoDebounceApiStatus ===
									'loading' ||
								updateRecapQuoteInfoApiCounter > 0 ? (
									<>
										<CachedIcon fontSize="small" />{' '}
										{linkSmall && 'Saving...'}
									</>
								) : (
									<>
										<CloudDoneOutlinedIcon fontSize="small" />{' '}
										{linkSmall && 'Saved'}
									</>
								)}
							</div>
						)}
						{getQuotePills()}
					</Grid>
				</Grid>
			</Toolbar>
		</AppBar>
	)
}

export default GridNavbar
